import {
    Component, Input, ChangeDetectionStrategy, OnDestroy, ChangeDetectorRef, ViewEncapsulation,
    AfterViewInit, ApplicationRef, QueryList, ViewChildren, NgZone, OnInit
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { gsap, Sine } from 'gsap';
import { ToastService } from '@core/state/toast/toast.service';
import { Toast } from '@core/models/toast/toast';
import { ToastType } from '@core/models/toast/toast-type';

@Component({
    selector: 'ihd-toast',
    templateUrl: './toast.component.html',
    styleUrls: ['./toast.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'c-notification-wrapper'
    }
})
export class ToastComponent implements OnInit, OnDestroy, AfterViewInit {

    public toasts: { id, toast }[] = [];
    public readonly ICONS: { [key: string]: string } = {
        [ToastType.INFO]: 'info',
        [ToastType.SUCCESS]: 'success',
        [ToastType.WARNING]: 'warning',
        [ToastType.ERROR]: 'error'
    };

    @Input('msg') msg?: string = '';
    @Input('position') position?: string = 'center';
    @Input('type') type?: string = 'success';

    @ViewChildren('toast') toastQuery: QueryList<any>;

    private delay: number = 3;
    private destroyed$: Subject<void> = new Subject();
    private animatingElements: HTMLElement[] = [];

    constructor(private toastService: ToastService,
                private cdr: ChangeDetectorRef,
                private appRef: ApplicationRef,
                private ngZone: NgZone) {
    }

    ngOnInit() {
        // Listen to all toasts
        this.toastService.getToasts()
            .pipe(
                takeUntil(this.destroyed$)
                )
            .subscribe((toast: Toast) => {
                if (toast) {
                    if (toast.type === ToastType.SUCCESS) {
                        this.toasts.length = 0;
                    }

                    this.toasts.unshift({
                        id: new Date().getTime(),
                        toast
                    });

                    this.toastService.flushToast();
                    this.cdr.markForCheck();
                }
            });
    }

    ngAfterViewInit() {

        // Listen for toast element changes
        this.toastQuery.changes.subscribe(() => {
            this.renderAnimation();
        });

        this.renderAnimation();
    }

    ngOnDestroy() {
        this.destroyed$.next();
    }

    /**
     * Render animation based on toast DOM elements.
     */
    renderAnimation() {
        // Include the element changes as animating
        let toastElements: HTMLElement[] = this.toastQuery
          .map((item) => item.nativeElement)
          .filter((toastElement) => this.animatingElements.indexOf(toastElement) < 0);

        this.animatingElements = this.animatingElements.concat(toastElements);
        let timeline: gsap.core.Timeline = gsap.timeline({
            autoRemoveChildren: true
        });
        timeline.eventCallback('onComplete', () => {
            this.ngZone.run(() => {
                if (toastElements) {

                    // Remove the completed elements
                    this.animatingElements = this.animatingElements.filter((animatingElement) => toastElements.indexOf(animatingElement) < 0);
                    if (this.animatingElements.length <= 0) {
                        this.animatingElements.length = 0;
                        this.toasts.length = 0;
                        this.cdr.markForCheck();
                        timeline.kill();
                    }
                }
            });
        });

        timeline
            .from(toastElements, {y: '-100%', opacity: 1, ease: Sine.easeOut, duration: .350 })
            .to(toastElements, {y: '-100%', autoAlpha: 0, ease: Sine.easeOut, duration: .200 }, `+=${this.delay}`);
    }

    /**
     * Track items via data identifier.
     * @param index
     * @param data
     */
    trackIdFn(index, data) {
        return data ? data.id : index;
    }

    /**
     * Manually closing toast.
     * @param item
     */
    close(item: any) {
        let ids = this.toasts.map((x) => x.id);
        let index = ids.indexOf(item.id);

        if (index > -1) {
            this.toasts.splice(index, 1);
        }
    }
}
